// Tickets — shared types, options, and badge tones for the Testing / Feedback /
// Tickets / Kanban surface (migration 0037).
//
// Access is role-gated (owner|admin|dev) at the RLS layer — that's the single
// source of truth for who can read/write; the UI doesn't gate affordances by
// role (a disallowed write is simply rejected by RLS). Every row still belongs
// to the single deployment owner — this is the one shared surface in an
// otherwise single-tenant app.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export type TicketKind = 'bug' | 'change_request' | 'feature_request';
export type TicketSeverity = 'low' | 'medium' | 'high' | 'critical';
export type TicketStatus = 'new' | 'in_review' | 'in_progress' | 'done';

export type Ticket = {
  id: string;
  title: string;
  description: string | null;
  feature_area: string | null;
  kind: TicketKind;
  severity: TicketSeverity;
  status: TicketStatus;
  created_by: string | null;
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
};

export type TicketComment = {
  id: string;
  ticket_id: string;
  author_id: string | null;
  body: string;
  created_at: string;
};

export type TicketAttachment = {
  id: string;
  ticket_id: string;
  storage_path: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

export const TICKET_SELECT =
  'id, title, description, feature_area, kind, severity, status, created_by, assignee_id, created_at, updated_at';

export const ATTACHMENT_BUCKET = 'ticket-attachments';

// The product surfaces a customer can give feedback on. "Something else / new"
// lets the owner file anything not in the list (free-text title carries it).
export const FEATURE_AREAS = [
  'Enter / Overview',
  'Inbox (email drafts)',
  'Messages (iMessage / WhatsApp)',
  'Meetings',
  'Agreements',
  'Action Items',
  'Focus Time',
  'Commissions',
  'Agent Health',
  'Intel',
  'Connections',
  'Settings',
  'Something else / new',
] as const;

export const KIND_OPTIONS: { value: TicketKind; label: string; hint: string }[] = [
  { value: 'bug', label: 'Bug', hint: "Something isn't working" },
  {
    value: 'change_request',
    label: 'Change request',
    hint: 'Works, but should behave differently',
  },
  { value: 'feature_request', label: 'New feature', hint: "Something that doesn't exist yet" },
];

export const SEVERITY_OPTIONS: { value: TicketSeverity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Kanban columns, in pipeline order. The board moves cards left → right.
export const STATUS_COLUMNS: { value: TicketStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
];

type Tone = { bg: string; text: string; label: string };

export function severityTone(s: TicketSeverity): Tone {
  switch (s) {
    case 'critical':
      return { bg: 'neo-chip bg-alert/10', text: 'text-alert', label: 'Critical' };
    case 'high':
      return { bg: 'neo-chip bg-orange-500/10', text: 'text-orange-600', label: 'High' };
    case 'medium':
      return { bg: 'neo-chip bg-learning/10', text: 'text-learning', label: 'Medium' };
    case 'low':
    default:
      return { bg: 'neo-chip bg-muted', text: 'text-muted-foreground', label: 'Low' };
  }
}

export function kindTone(k: TicketKind): Tone {
  switch (k) {
    case 'bug':
      return { bg: 'neo-chip bg-alert/10', text: 'text-alert', label: 'Bug' };
    case 'change_request':
      return { bg: 'neo-chip bg-learning/10', text: 'text-learning', label: 'Change' };
    case 'feature_request':
    default:
      return { bg: 'neo-chip bg-forest-soft', text: 'text-forest', label: 'Feature' };
  }
}

export function statusTone(s: TicketStatus): Tone {
  switch (s) {
    case 'new':
      return { bg: 'neo-chip bg-muted', text: 'text-muted-foreground', label: 'New' };
    case 'in_review':
      return { bg: 'neo-chip bg-learning/10', text: 'text-learning', label: 'In Review' };
    case 'in_progress':
      return { bg: 'neo-chip bg-sage/10', text: 'text-sage', label: 'In Progress' };
    case 'done':
    default:
      return { bg: 'neo-chip bg-forest-soft', text: 'text-forest', label: 'Done' };
  }
}

/**
 * The signed-in user's team role (owner|admin|dev|null). RLS is the real access
 * gate; this is used only to decide which UI affordances to show (e.g. the
 * Delete action, which RLS restricts to owner|admin).
 */
export function useTeamRole(): { role: string | null; isLoading: boolean } {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['team-role', user?.id],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase.from('users').select('role').eq('id', user!.id).maybeSingle();
      return (data as { role: string | null } | null)?.role ?? null;
    },
  });
  return { role: q.data ?? null, isLoading: q.isLoading };
}

export async function fetchTickets(): Promise<Ticket[]> {
  const { data, error } = await supabase
    .from('tickets')
    .select(TICKET_SELECT)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as unknown as Ticket[]) ?? [];
}
